<template>
  <div class="notifications-sidebar" :class="{ hidden: !isVisible }" role="region" :aria-label="$t('notifications.title')">
    <!-- Sticky header -->
    <div class="sidebar-topbar">
      <h3 class="sidebar-title">{{ $t('notifications.title') }}</h3>
      <button class="icon-btn close" @click="$emit('close')" :aria-label="$t('notifications.close')">&times;</button>
    </div>

    <!-- Tabs -->
    <div class="nc-tabs" role="tablist">
      <button class="nc-tab" :class="{ active: tab === 'live' }" role="tab" @click="switchTab('live')">
        {{ $t('notifications.tabs.live') }}<span v-if="liveItems.length" class="nc-count">{{ liveItems.length }}</span>
      </button>
      <button class="nc-tab" :class="{ active: tab === 'allen' }" role="tab" @click="switchTab('allen')">
        <i class="bi bi-robot"></i> {{ $t('notifications.tabs.allen') }}<span v-if="allenItems.length" class="nc-count nc-count-allen">{{ allenItems.length }}</span>
      </button>
    </div>

    <!-- One header row for BOTH tabs -->
    <div class="nc-actions" v-if="currentItems.length">
      <button class="nc-link" type="button" @click="toggleSelectAll">{{ allSelected ? $t('notifications.deselectAll') : $t('notifications.selectAll') }}</button>
      <button class="nc-danger" type="button" :disabled="!selectedCount" @click="clearSelected">
        {{ $t('notifications.clearSelected') }}<span v-if="selectedCount"> ({{ selectedCount }})</span>
      </button>
    </div>

    <!-- One card design for BOTH tabs. Click toggles selection; primary action = button. -->
    <div class="nc-list" v-if="currentItems.length">
      <div
        v-for="n in currentItems"
        :key="keyOf(n)"
        class="nc-card"
        :class="[tab === 'allen' ? 'sev-' + n.severity : 'live', { selected: isSelected(n) }]"
        role="button"
        tabindex="0"
        :aria-pressed="isSelected(n)"
        @click="toggleSelect(n)"
        @keydown.enter="toggleSelect(n)"
      >
        <span class="nc-accent" :style="{ background: accentOf(n) }"></span>
        <span class="nc-check"><i class="bi" :class="isSelected(n) ? 'bi-check-circle-fill' : 'bi-circle'"></i></span>
        <div class="nc-icon"><i :class="iconOf(n)" :style="{ color: accentOf(n) }"></i></div>
        <div class="nc-body">
          <template v-if="tab === 'live'">
            <component :is="getNotificationComponent(n.DetectionZoneTypeId)" :zone="n.DetectionZoneName" :time="n.PassedTime" />
            <div class="nc-meta"><i class="bi bi-clock"></i> {{ fmtDT(n.TimeStamp) }}</div>
          </template>
          <template v-else>
            <span class="nc-title">{{ n.title }}</span>
            <span class="nc-detail">{{ n.detail }}</span>
            <div class="nc-meta"><i class="bi bi-clock"></i> {{ fmtDT(n.createdAt || n.CreatedAt) }}</div>
            <div v-if="n.when" class="nc-when"><i class="bi bi-arrow-repeat"></i> {{ n.when }}</div>
          </template>

          <!-- Per-card primary action — always visible (works on mobile). Live:
               Acknowledge (only on zones that support it, 4/5/6/7/8; prod pauses the
               edge). Allen: View — a light link (navigation, not a heavy action). -->
          <div class="nc-cardactions" v-if="tab === 'allen'">
            <button class="nc-action nc-action--ghost" type="button" @click.stop="primary(n)">
              {{ $t('notifications.view') }} <i class="bi bi-arrow-right"></i>
            </button>
          </div>
          <div class="nc-cardactions" v-else-if="shouldShowAcknowledge(n.DetectionZoneTypeId)">
            <button class="nc-action" type="button" @click.stop="primary(n)">
              {{ $t('notifications.acknowledge') }} <i class="bi bi-check2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty states -->
    <div v-else-if="tab === 'live'" class="empty-state">
      <span class="zen-icon">🧘</span>
      <p>{{ $t('notifications.empty.liveLine1') }}<br/>{{ $t('notifications.empty.liveLine2') }}</p>
    </div>
    <div v-else class="empty-state">
      <span class="zen-icon">✅</span>
      <p>{{ $t('notifications.empty.allenLine1') }}<br/>{{ $t('notifications.empty.allenLine2') }}</p>
    </div>
  </div>
</template>

<script>
    import { ref, onMounted, computed } from 'vue'
    import { useRouter } from 'vue-router'
    import { useNotificationCenterStore } from '@/stores/NotificationCenterStore'
    import { currentLanguage } from '@/i18n'

    import ActionNotificationEntranceZone from '@/components/action_notifications/ActionNotificationEntranceZone.vue'
    import ActionNotificationExitZone from '@/components/action_notifications/ActionNotificationExitZone.vue'
    import ActionNotificationCombinedEEZone from '@/components/action_notifications/ActionNotificationCombinedEEZone.vue'
    import ActionNotificationWaitingZone from '@/components/action_notifications/ActionNotificationWaitingZone.vue'
    import ActionNotificationInteractionZone from '@/components/action_notifications/ActionNotificationInteractionZone.vue'
    import ActionNotificationPDDZone from '@/components/action_notifications/ActionNotificationPDDZone.vue'

    export default {
        name: 'NotificationsSidebar',
        props: { isVisible: { type: Boolean, default: true } },
        emits: ['close'],

        setup(props, { emit }) {
            const router = useRouter()
            const nc = useNotificationCenterStore()

            const tab = computed(() => nc.activeTab)
            const selectedKeys = ref([])
            const switchTab = (t) => { selectedKeys.value = []; nc.setTab(t) }

            // ---- sources (one shared store) ----
            const liveItems = computed(() => nc.live)
            const allenItems = computed(() => nc.allen)
            const currentItems = computed(() => tab.value === 'live' ? liveItems.value : allenItems.value)

            // ---- selection (identical model for both tabs) ----
            const keyOf = (n) => tab.value === 'live' ? 'L' + n.NotificationId : 'A' + n.id
            const isSelected = (n) => selectedKeys.value.includes(keyOf(n))
            const toggleSelect = (n) => {
                const k = keyOf(n)
                const i = selectedKeys.value.indexOf(k)
                if (i >= 0) selectedKeys.value.splice(i, 1); else selectedKeys.value.push(k)
            }
            const selectedCount = computed(() => selectedKeys.value.length)
            const allSelected = computed(() =>
                currentItems.value.length > 0 && currentItems.value.every((n) => isSelected(n)))
            const toggleSelectAll = () => {
                selectedKeys.value = allSelected.value ? [] : currentItems.value.map(keyOf)
            }
            const clearSelected = () => {
                if (tab.value === 'live') {
                    nc.clearLive(currentItems.value.filter(isSelected).map((n) => n.NotificationId))
                } else {
                    nc.clearAllen(currentItems.value.filter(isSelected).map((n) => n.id))
                }
                selectedKeys.value = []
            }

            // ---- per-card primary action (leaves the inbox) ----
            const primary = (n) => {
                if (tab.value === 'live') {
                    nc.acknowledgeLive(n.NotificationId)   // prod: + edge action_acknowledge_zone + toast
                } else {
                    nc.viewAllen(n.id)                     // prod: mark read/dismissed
                    emit('close')
                    if (n.deepLink) router.push(n.deepLink)
                }
            }

            // Generation timestamp — "13 Jul 2026, 09:42" (demo dates are dynamic; prod
            // sends the real TimeStamp / CreatedAt).
            const fmtDT = (iso) => {
                if (!iso) return ''
                const d = new Date(iso)
                if (isNaN(d)) return ''
                // English keeps the browser's own format (as before); Dutch readers get Dutch months.
                return d.toLocaleString(currentLanguage() === 'nl' ? 'nl' : undefined, {
                    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                })
            }

            // ---- visuals ----
            const shouldShowAcknowledge = (typeId) => [4, 5, 6, 7, 8].includes(typeId)
            const isCriticalZone = (typeId) => [4, 5, 6, 7, 8].includes(typeId)
            const SEV_COLOR = { info: '#62B5D9', good: '#6C9046', warning: '#D3A741', high: '#C21500' }
            const SEV_ICON = {
                info: 'bi bi-info-circle-fill', good: 'bi bi-check-circle-fill',
                warning: 'bi bi-exclamation-triangle-fill', high: 'bi bi-exclamation-octagon-fill',
            }
            const accentOf = (n) => tab.value === 'live'
                ? (isCriticalZone(n.DetectionZoneTypeId) ? '#D3A741' : '#62B5D9')
                : (SEV_COLOR[n.severity] || '#8fa2b7')
            const iconOf = (n) => tab.value === 'live'
                ? (isCriticalZone(n.DetectionZoneTypeId) ? 'bi bi-exclamation-triangle-fill' : 'bi bi-info-circle-fill')
                : (SEV_ICON[n.severity] || 'bi bi-info-circle-fill')

            const getNotificationComponent = (typeId) => {
                switch (typeId) {
                    case 1: return ActionNotificationEntranceZone
                    case 2: return ActionNotificationExitZone
                    case 3: return ActionNotificationCombinedEEZone
                    case 4:
                    case 9:
                    case 10:
                    case 11: return ActionNotificationWaitingZone
                    case 5: return ActionNotificationInteractionZone
                    case 6:
                    case 7:
                    case 8: return ActionNotificationPDDZone
                    default: return ActionNotificationEntranceZone
                }
            }

            onMounted(() => nc.seedDemo())

            return {
                tab, switchTab,
                liveItems, allenItems, currentItems,
                keyOf, isSelected, toggleSelect, selectedCount, allSelected, toggleSelectAll, clearSelected,
                primary, shouldShowAcknowledge, fmtDT,
                accentOf, iconOf, getNotificationComponent,
            }
        }
    }
</script>

<style scoped>
/* Shell */
.notifications-sidebar{
  position: fixed; right: 0; top: 0; height: 100vh; width: 400px;
  background: #ffffff; border-left: 1px solid #e6edf3;
  box-shadow: -6px 0 18px rgba(13,38,76,.10); z-index: 9999;
  display: flex; flex-direction: column; overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable;
}
.notifications-sidebar.hidden{ display:none; }

/* Topbar */
.sidebar-topbar{
  position: sticky; top: 0; z-index: 3;
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px; background: #ffffff; border-bottom: 1px solid #e6edf3;
}
.sidebar-title{ margin:0; font-size: 1.05rem; font-weight: 700; color: #4F7EB3; }
.icon-btn.close{
  position: absolute; top: 10px; right: 10px; width: 28px; height: 28px; border-radius: 50%;
  border: 1px solid #d7dee6; background: #eef2f7; color: #425461;
  display: inline-flex; align-items: center; justify-content: center; line-height: 1; font-size: 16px; cursor: pointer; z-index: 2;
  transition: background .15s ease, border-color .15s ease, color .15s ease;
}
.icon-btn.close:hover{ background: #4F7EB3; border-color: #4F7EB3; color: #fff; }

/* Tabs */
.nc-tabs{
  position: sticky; top: 56px; z-index: 2;
  display: flex; gap: 4px; padding: 8px 12px 0; background: #ffffff; border-bottom: 1px solid #e6edf3;
}
.nc-tab{
  flex: 1 1 0; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 9px 8px; border: 0; background: transparent; cursor: pointer;
  font-size: .9rem; font-weight: 600; color: #7b8b99; border-bottom: 2px solid transparent; margin-bottom: -1px;
}
.nc-tab:hover{ color: #4F7EB3; }
.nc-tab.active{ color: #4F7EB3; border-bottom-color: #4F7EB3; }
.nc-tab .bi{ font-size: .95rem; }
.nc-count{
  min-width: 18px; height: 18px; padding: 0 5px; border-radius: 999px;
  background: #cfd8e2; color: #33404d; font-size: .7rem; font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center;
}
.nc-tab.active .nc-count{ background: #4F7EB3; color: #fff; }
.nc-count-allen{ background: #4F7EB3; color: #fff; }

/* Header row (both tabs) */
.nc-actions{
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 18px 16px 12px; background: #fff; border-bottom: 1px solid #e6edf3;
}
.nc-link{ background: transparent; border: 0; color: #4F7EB3; font-size: .85rem; font-weight: 600; cursor: pointer; padding: 2px; text-decoration: underline; }
.nc-link:hover{ color: #3a6699; }
.nc-danger{
  display: inline-flex; align-items: center; gap: 6px;
  background: #fff; color: #C21500; border: 1px solid #C21500; border-radius: 8px;
  padding: 6px 10px; font-size: .85rem; font-weight: 600; cursor: pointer;
}
.nc-danger:hover:not(:disabled){ background: #C21500; color: #fff; }
.nc-danger:disabled{ opacity: .4; cursor: not-allowed; }

/* One card */
.nc-list{ display: block; padding: 14px 14px 24px; }
.nc-card{
  position: relative; display: grid; grid-template-columns: 8px 30px 1fr; gap: 10px;
  background: #fff; border: 1px solid #e6edf3; border-radius: 12px; box-shadow: 0 3px 12px rgba(13,38,76,.06);
  padding: 12px 12px 12px 0; margin-bottom: 16px; cursor: pointer;
  transition: transform .08s ease, box-shadow .2s ease, border-color .2s ease;
}
.nc-card:last-child{ margin-bottom: 0; }
.nc-card:hover{ transform: translateY(-1px); box-shadow: 0 6px 18px rgba(13,38,76,.10); }
.nc-card:focus-visible{ outline: 2px solid #B2C149; outline-offset: 2px; }
.nc-card.selected{ border-color: #4F7EB3; box-shadow: 0 6px 18px rgba(79,126,179,.18); }

.nc-accent{ grid-column: 1; align-self: stretch; width: 8px; border-top-left-radius: 12px; border-bottom-left-radius: 12px; }
.nc-check{ position: absolute; top: 8px; right: 8px; font-size: 18px; color: #c3ccd6; }
.nc-card.selected .nc-check{ color: #4F7EB3; }
.nc-icon{ grid-column: 2; display: flex; align-items: flex-start; justify-content: center; padding-top: 2px; }
.nc-icon i{ font-size: 20px; }

.nc-body{ grid-column: 3; display: flex; flex-direction: column; gap: 3px; min-width: 0; color: #425461; font-size: .92rem; padding-right: 22px; }
.nc-title{ font-weight: 700; font-size: .9rem; color: #1d2b3a; line-height: 1.3; }
.nc-detail{ font-size: .8rem; color: #51637a; line-height: 1.4; }
.nc-meta{ margin-top: 4px; font-size: .72rem; color: #8393a1; font-style: italic; display: inline-flex; align-items: center; gap: 5px; }
.nc-meta i{ font-style: normal; font-size: .68rem; }
/* Trend-window pill (Allen) — the analytical timeframe, distinct from the generation time. */
.nc-when{
  align-self: flex-start; margin-top: 5px; font-size: .68rem; color: #6b7a86;
  background: #eef2f6; border-radius: 999px; padding: .1rem .5rem;
  display: inline-flex; align-items: center; gap: .28rem;
}
.nc-when i{ font-size: .62rem; }

/* Per-card actions */
.nc-cardactions{ margin-top: 10px; }
.nc-action{
  display: inline-flex; align-items: center; gap: 6px;
  background: #4F7EB3; color: #fff; border: 0; border-radius: 8px;
  padding: 6px 10px; font-size: .85rem; font-weight: 600; cursor: pointer; transition: background .2s ease;
}
.nc-action:hover{ background: #3a6699; }
.nc-action .bi{ font-size: .85rem; }
/* Ghost variant — a light link-style action (Allen "View"). */
.nc-action--ghost{ background: transparent; color: #4F7EB3; padding: 2px 2px; font-weight: 600; }
.nc-action--ghost:hover{ background: transparent; color: #3a6699; text-decoration: underline; }

/* Empty state */
.empty-state{ flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; color:#6c757d; padding: 0 24px; }
.zen-icon{ font-size: 64px; filter: grayscale(1) brightness(1.15); margin-bottom: 12px; }

@media (max-width: 480px){
  .notifications-sidebar{ width: 100%; }
  .nc-tabs{ top: 56px; }
}
</style>
